import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, TextField, Select, MenuItem, InputLabel, FormControl,
  TablePagination, Grid, InputAdornment, Typography, Chip, Tooltip, Box,
} from "@mui/material";
import { Search, Download, ErrorOutline, CheckCircle, GavelOutlined, KeyboardArrowDown, KeyboardArrowRight, OpenInNew } from "@mui/icons-material";
import { Collapse, IconButton } from "@mui/material";
import { downloadCSV } from "./csv";

const API_BASE = process.env.REACT_APP_API_BASE || "https://innovators.onrender.com";

// Chargebacks, as recorded by the automated evidence handler in the backend.
//
//   status   — what WE did: submitted | needs_review | failed | processing
//   outcome  — what the card network decided: won | lost | warning_closed
//
// They are deliberately separate: "we responded" is not "we won", and the win
// rate below is only meaningful when measured over the cases we actually
// submitted evidence for.

// What the cardholder told their bank, as far as Stripe passes it on.
//
// Stripe exposes the network's reason CODE and nothing else — there is no
// field anywhere in the dispute object carrying the customer's own wording,
// so these sentences are the closest honest rendering of the claim. If the
// bank sent a written statement it is a file on the dispute in the Stripe
// Dashboard, not something the API hands over.
const REASON_CLAIM = {
  product_not_received: "They told their bank they never received the course.",
  product_unacceptable: "They told their bank the course was not as described.",
  fraudulent: "They told their bank they did not make or authorise this payment.",
  unrecognized: "They told their bank they did not recognise the charge.",
  duplicate: "They told their bank they were charged twice for the same thing.",
  subscription_canceled: "They told their bank they had cancelled before being charged.",
  credit_not_processed: "They told their bank a refund they were promised never arrived.",
  customer_initiated: "The cardholder raised this directly with their bank.",
  general: "No specific reason was given by the bank.",
  debit_not_authorized: "They told their bank the payment was taken without authorisation.",
  bank_cannot_process: "Their bank could not process the payment.",
  insufficient_funds: "The payment failed for insufficient funds.",
  incorrect_account_details: "The account details were wrong.",
  check_returned: "A cheque was returned.",
};

// The evidence fields we fill in, in the order a reviewer reads them.
const EVIDENCE_LABELS = {
  product_description: "What they bought",
  service_date: "Service date",
  customer_name: "Customer name",
  customer_email_address: "Customer email",
  uncategorized_text: "Our argument",
  access_activity_log: "Access activity log",
};

const money = (minor, currency) =>
  `${(Number(minor || 0) / 100).toFixed(2)} ${String(currency || "gbp").toUpperCase()}`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// Stripe gives evidence_details.due_by as a Unix timestamp in seconds.
const daysUntil = (unixSeconds) => {
  if (!unixSeconds) return null;
  return Math.ceil((unixSeconds * 1000 - Date.now()) / 86400000);
};

/*
  WHAT STRIPE THINKS, NOT ONLY WHAT WE DID.

  `status` is our own record of what we did about a dispute. It goes stale the
  moment a webhook does not arrive — an enquiry that escalates to a chargeback
  leaves us saying "Evidence sent" while Stripe sits waiting for a response,
  and the row looks handled until the deadline passes. That is how
  du_1UNUTm… went unanswered for a week.

  `stripeStatus` is written by the nightly reconcile. Where the two disagree
  the dispute needs a human NOW, whatever our own status says.
*/
const AWAITING_US = ["needs_response", "warning_needs_response"];
const OUR_OPEN = ["needs_review", "failed", "processing"];
const needsResponse = (d) =>
  AWAITING_US.includes(d.stripeStatus) && !OUR_OPEN.includes(d.status) && !d.outcome;

const statusChip = (status) => {
  switch (status) {
    case "submitted":
      return <Chip size="small" color="primary" icon={<CheckCircle />} label="Evidence sent" />;
    case "needs_review":
      return <Chip size="small" color="warning" icon={<ErrorOutline />} label="Needs review" />;
    case "failed":
      return <Chip size="small" color="error" icon={<ErrorOutline />} label="Failed" />;
    case "processing":
      return <Chip size="small" label="Processing…" />;
    case "closed_without_record":
      return <Tooltip title="Closed before we had a record of it"><Chip size="small" label="No record" /></Tooltip>;
    default:
      return <Chip size="small" label={status || "—"} />;
  }
};

const outcomeChip = (outcome) => {
  switch (outcome) {
    case "won":
      return <Chip size="small" color="success" icon={<CheckCircle />} label="Won" />;
    case "lost":
      return <Chip size="small" color="error" icon={<ErrorOutline />} label="Lost" />;
    case "warning_closed":
      return <Tooltip title="Early warning that never became a chargeback"><Chip size="small" label="Warning closed" /></Tooltip>;
    default:
      return <Chip size="small" variant="outlined" label="Open" />;
  }
};

/**
 * Everything behind one row: their claim on the left, our answer on the right.
 *
 * This is the screen you want open when a dispute is lost and somebody asks
 * what we actually argued — and the one that makes a needs_review case
 * actionable, because it shows the LMS picture the auto-responder saw.
 */
function DisputeDetail({ d }) {
  const ev = d.evidenceSent || null;
  const act = d.activity || null;
  const claim = REASON_CLAIM[d.reason] || "No reason code was supplied by the bank.";

  const panel = {
    flex: "1 1 320px",
    minWidth: 280,
    background: "#fff",
    border: "1px solid #e3e7ee",
    borderRadius: 8,
    padding: "12px 14px",
  };
  const heading = { fontWeight: 800, fontSize: 12, letterSpacing: 0.4, textTransform: "uppercase" };

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", p: 2, background: "#f4f6fa" }}>
      {/* ── their side ── */}
      <div style={panel}>
        <Typography sx={{ ...heading, color: "#b3261e", mb: 1 }}>What the candidate said</Typography>

        <Typography variant="body2" sx={{ mb: 1.5 }}>{claim}</Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Reason code: <code>{d.reason || "—"}</code>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Amount: {money(d.amount, d.currency)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Opened: {fmt(d.createdAtISO)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Respond by: {d.dueBy ? new Date(d.dueBy * 1000).toLocaleString() : "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
          Stripe does not pass on the cardholder&apos;s own wording. If their bank sent a written
          statement it is attached to this dispute in the Stripe Dashboard.
        </Typography>
      </div>

      {/* ── our side ── */}
      <div style={panel}>
        <Typography sx={{ ...heading, color: "#1565c0", mb: 1 }}>
          {ev ? "Proof we sent Stripe" : "Nothing sent yet"}
        </Typography>

        {!ev && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {d.detail || "No evidence has been submitted for this dispute."}
          </Typography>
        )}

        {ev && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Submitted {fmt(d.submittedAtISO)}
            </Typography>
            {Object.entries(EVIDENCE_LABELS).map(([field, label]) =>
              ev[field] ? (
                <Box key={field} sx={{ mb: 1.2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>{label}</Typography>
                  {field === "access_activity_log" ? (
                    /* The long one. Monospaced and scrollable rather than
                       truncated — this is the exhibit, not a summary. */
                    <Box
                      component="pre"
                      sx={{
                        m: 0, mt: 0.5, p: 1, maxHeight: 220, overflow: "auto",
                        background: "#0f1720", color: "#d7e2ea", borderRadius: 1,
                        fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
                      }}
                    >
                      {ev[field]}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: 12.5 }}>{ev[field]}</Typography>
                  )}
                </Box>
              ) : null
            )}
          </>
        )}

        {/* The LMS picture, shown even when nothing was submitted — this is
            exactly what a reviewer needs to decide a needs_review case. */}
        {act && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed #e3e7ee" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              Learning platform record
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {d.summary?.videos_watched ?? 0} videos · {d.summary?.downloads ?? 0} downloads ·{" "}
              {Math.round((d.summary?.total_watch_time_seconds || 0) / 60)} min watched
              {act.refundEligibility ? ` · refund eligibility: ${act.refundEligibility}` : ""}
            </Typography>
            {(act.videos || []).slice(0, 6).map((v, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                • {v.video_title} — {v.percentage_watched}% ({Math.round((v.total_watch_time || 0) / 60)} min)
              </Typography>
            ))}
            {(act.downloads || []).slice(0, 6).map((f, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                • downloaded {f.file_name} on {f.download_date} from {f.ip_address}
              </Typography>
            ))}
          </Box>
        )}
      </div>
    </Box>
  );
}

const DisputesTable = () => {
  const [disputes, setDisputes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openRow, setOpenRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  /*
    FETCHED FROM THE API, NOT FROM FIRESTORE.

    This app authenticates against a different Firebase project to the one the
    payments server writes disputes into, so reading the collection directly
    returned nothing at all — the table was empty while the records existed.
    Moving 600+ customer documents between projects to fix that would be a far
    bigger risk than asking the server for what it already owns.

    The cost is losing the live listener, so there is a Refresh button.
  */
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/disputes`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Could not load disputes");
      setDisputes(Array.isArray(data.disputes) ? data.disputes : []);
    } catch (err) {
      setLoadError(err.message || "Could not load disputes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return disputes.filter((d) => {
      const matchesSearch = !q
        ? true
        : [d.email, d.courseName, d.disputeId, d.chargeId, d.reason, d.detail]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
      const matchesStatus = statusFilter ? d.status === statusFilter : true;
      const matchesOutcome =
        outcomeFilter === "open" ? !d.outcome : outcomeFilter ? d.outcome === outcomeFilter : true;
      return matchesSearch && matchesStatus && matchesOutcome;
    });
  }, [disputes, searchQuery, statusFilter, outcomeFilter]);

  useEffect(() => setPage(0), [searchQuery, statusFilter, outcomeFilter]);

  // Win rate is only honest over closed cases we actually argued.
  const stats = useMemo(() => {
    const closed = disputes.filter((d) => d.outcome === "won" || d.outcome === "lost");
    const argued = closed.filter((d) => d.evidenceAutoSubmitted);
    const wonArgued = argued.filter((d) => d.outcome === "won").length;
    return {
      total: disputes.length,
      open: disputes.filter((d) => !d.outcome).length,
      needsReview: disputes.filter((d) => d.status === "needs_review" && !d.outcome).length,
      awaitingReply: disputes.filter(needsResponse).length,
      won: closed.filter((d) => d.outcome === "won").length,
      lost: closed.filter((d) => d.outcome === "lost").length,
      debt: disputes.filter((d) => d.needsDebtRecovery).length,
      argued: argued.length,
      winRate: argued.length ? Math.round((wonArgued / argued.length) * 100) : null,
    };
  }, [disputes]);

  const handleExport = () =>
    downloadCSV(
      filtered.map((d) => ({
        opened: d.createdAtISO || "",
        disputeId: d.disputeId || d.id,
        email: d.email || "",
        course: d.courseName || "",
        amount: (Number(d.amount || 0) / 100).toFixed(2),
        currency: String(d.currency || "").toUpperCase(),
        reason: d.reason || "",
        handling: d.status || "",
        outcome: d.outcome || "open",
        evidenceAutoSubmitted: d.evidenceAutoSubmitted ? "yes" : "no",
        needsDebtRecovery: d.needsDebtRecovery ? "yes" : "no",
        detail: d.detail || "",
      })),
      "disputes.csv"
    );

  const visibleRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div>
      <Typography variant="h4" align="center" sx={{ mb: 2 }}>
        <GavelOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
        Disputes
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Chip label={`Total: ${stats.total}`} />
        <Chip variant="outlined" label={`Open: ${stats.open}`} />
        <Chip color="warning" label={`Needs review: ${stats.needsReview}`} />
        {/* Only ever shown when it is not zero: a permanent "0" trains you to
            ignore the one time it says 1. */}
        {stats.awaitingReply > 0 && (
          <Tooltip title="Stripe is waiting for a response on these, whatever our own record says">
            <Chip color="error" icon={<ErrorOutline />} label={`Stripe awaiting reply: ${stats.awaitingReply}`} />
          </Tooltip>
        )}
        <Chip color="success" label={`Won: ${stats.won}`} />
        <Chip color="error" label={`Lost: ${stats.lost}`} />
        <Tooltip title="Won ÷ closed, counting only disputes where evidence was auto-submitted">
          <Chip
            color="primary"
            label={stats.winRate === null ? "Win rate: no data yet" : `Win rate: ${stats.winRate}% (${stats.argued} argued)`}
          />
        </Tooltip>
        <Chip color="error" variant="outlined" label={`Debt recovery: ${stats.debt}`} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            placeholder="Search email, course, dispute id, reason"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Our handling</InputLabel>
            <Select label="Our handling" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="needs_review">Needs review</MenuItem>
              <MenuItem value="submitted">Evidence sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Outcome</InputLabel>
            <Select label="Outcome" value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="won">Won</MenuItem>
              <MenuItem value="lost">Lost</MenuItem>
              <MenuItem value="warning_closed">Warning closed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={1}>
          <Button fullWidth variant="outlined" onClick={load} disabled={loading}>
            {loading ? "…" : "Refresh"}
          </Button>
        </Grid>
        <Grid item xs={6} md={1}>
          <Button fullWidth variant="outlined" color="success" startIcon={<Download />} onClick={handleExport}>
            CSV
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff", width: 40 }} />
              {["Opened", "Respond by", "Customer", "Course", "Amount", "Reason", "Our handling", "Outcome", "Notes"].map((h) => (
                <TableCell key={h} sx={{ color: "#fff" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color={loadError ? "error" : "text.secondary"} sx={{ py: 2 }}>
                    {loading ? "Loading disputes…"
                      : loadError ? `Could not load disputes: ${loadError}`
                      : "No disputes match this filter."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((d, i) => {
              const left = daysUntil(d.dueBy);
              const stillOpen = !d.outcome;
              const expanded = openRow === d.id;
              return (
                <React.Fragment key={d.id}>
                <TableRow
                  hover
                  onClick={() => setOpenRow(expanded ? null : d.id)}
                  sx={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#fff", cursor: "pointer" }}
                >
                  <TableCell sx={{ width: 40 }}>
                    <IconButton size="small" aria-label={expanded ? "Hide detail" : "Show detail"}>
                      {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{fmt(d.createdAtISO)}</TableCell>
                  <TableCell>
                    {!d.dueBy ? "—" : stillOpen ? (
                      <Chip
                        size="small"
                        color={left <= 2 ? "error" : left <= 5 ? "warning" : "default"}
                        label={left < 0 ? "Overdue" : `${left}d left`}
                      />
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {d.email || "—"}
                    {/* The doc id IS the Stripe dispute id, so the link needs no stored URL.
                        stopPropagation keeps the row from expanding when the link is clicked. */}
                    {String(d.id || "").startsWith("du_") && (
                      <Tooltip title="Open in Stripe">
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://dashboard.stripe.com/disputes/${d.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Open dispute ${d.id} in Stripe`}
                          sx={{ ml: 0.5 }}
                        >
                          <OpenInNew sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{d.courseName || "—"}</TableCell>
                  <TableCell>{money(d.amount, d.currency)}</TableCell>
                  <TableCell>{d.reason || "—"}</TableCell>
                  <TableCell>
                    {needsResponse(d) && (
                      <Tooltip title={`Stripe is still waiting: ${d.stripeStatus}. Our record says "${d.status}" — answer this one.`}>
                        <Chip size="small" color="error" icon={<ErrorOutline />} label="Stripe awaiting reply"
                          sx={{ mb: 0.5, display: "flex" }} />
                      </Tooltip>
                    )}
                    {statusChip(d.status)}
                  </TableCell>
                  <TableCell>
                    {outcomeChip(d.outcome)}
                    {d.needsDebtRecovery && (
                      <Tooltip title="Lost — still owed under our terms. Send a demand letter.">
                        <Chip size="small" color="error" variant="outlined" label="Recover" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={d.detail || ""}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {d.detail || "—"}
                      </Typography>
                    </Tooltip>
                    {d.summary && (
                      <Typography variant="caption" color="text.secondary">
                        {d.summary.videos_watched ?? 0} videos · {d.summary.downloads ?? 0} downloads
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                {/* Kept mounted so the scroll position inside the activity
                    log survives collapsing and reopening a row. */}
                <TableRow>
                  <TableCell colSpan={10} sx={{ p: 0, borderBottom: expanded ? undefined : "none" }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <DisputeDetail d={d} />
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 2, py: 1, display: "block" }}>
          Click a row to see their claim and the proof we sent.
        </Typography>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </TableContainer>
    </div>
  );
};

export default DisputesTable;
